import { StatusData } from "@fairfleet/geotab";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { readingMs } from "@/pages/live-data/utilities/newestReading";
import { fetchStatusDataComplete } from "@/utilities/query/fetchStatusDataComplete";

/**
 * How far before the newest reading a refresh starts reading again.
 *
 * A unit uploads in order, but not every diagnostic in the same message: a reading can arrive
 * shortly after one with a later timestamp. A unit that was offline sends its backlog with
 * timestamps after the newest one already here, so that is covered without any overlap.
 */
export const REFRESH_OVERLAP_MS = 10 * 60 * 1000;

/**
 * Where a refresh starts reading: shortly before the newest reading already loaded, or the start
 * of the period when nothing is loaded yet.
 *
 * @param previous - What is loaded for this vehicle and period, oldest first.
 */
export function refreshStart(previous: StatusData[] | undefined, fromDate: Date): Date {
  const newest = previous?.[previous.length - 1];
  const newestMs = newest ? readingMs(newest.dateTime) : NaN;
  if (!Number.isFinite(newestMs)) {
    return fromDate;
  }
  return new Date(Math.max(fromDate.getTime(), newestMs - REFRESH_OVERLAP_MS));
}

/**
 * The loaded readings with everything from `startMs` on replaced by a fresh read of that part.
 *
 * Replaced rather than merged: the fresh read covers that part of the period completely, so
 * keeping only the older readings needs no comparison of ids and cannot double a reading.
 *
 * @param previous - Oldest first.
 * @param fresh - Oldest first, read from `startMs` to the end of the period.
 */
export function replaceFrom(previous: StatusData[], fresh: StatusData[], startMs: number) {
  const kept = previous.filter(reading => readingMs(reading.dateTime) < startMs);
  for (const reading of fresh) {
    kept.push(reading);
  }
  return kept;
}

/**
 * Every reading a vehicle sent over a period, across all its diagnostics.
 *
 * Through `fetchStatusDataComplete`, which pages forward until the window is covered. Geotab
 * returns readings oldest first and silently truncates at the results limit, so a single request
 * for a busy vehicle loses the newest — and most interesting — part of the period. That utility
 * is also where the retry and abort behaviour already lives.
 *
 * A refresh of the same vehicle and period reads only what is new (see {@link refreshStart}):
 * re-reading a long period every 30 seconds costs many requests for a handful of new readings.
 *
 * @param deviceId - The vehicle, or `undefined` while none is picked.
 * @param fromDate - Start of the period.
 * @param toDate - End of the period.
 * @param refetchIntervalMs - How often to read the period again, or `false` not to.
 */
export function useMotorStatusData(
  deviceId: string | undefined,
  fromDate: Date,
  toDate: Date,
  refetchIntervalMs: number | false = false,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const queryClient = useQueryClient();

  return useQuery({
    // Timestamps rather than the dates themselves: React Query hashes the key by value, and two
    // Date objects for the same instant do not hash alike.
    queryKey: [
      "motorStatusData",
      activeSessionId,
      deviceId,
      fromDate.getTime(),
      toDate.getTime(),
    ] as const,

    queryFn: async ({ signal, queryKey }) => {
      // This key's own data, never the placeholder carried over from another period.
      const previous = queryClient.getQueryData<StatusData[]>(queryKey);
      const start = refreshStart(previous, fromDate);
      const fresh: StatusData[] = [];

      await fetchStatusDataComplete(geotabClient, {
        // One series, no diagnostic filter: the page is about finding out what a vehicle reports
        // at all, so it cannot name the diagnostics up front.
        tasks: [{ key: "all", deviceId: deviceId as string }],
        fromDate: start,
        toDate,
        signal,
        onPage: (_key, page) => fresh.push(...page),
      });

      // Oldest first, which the chart needs. Compared without building a Date per comparison: a busy
      // vehicle has on the order of 10^5 readings, and the comparator runs n log n times.
      fresh.sort((a, b) => readingMs(a.dateTime) - readingMs(b.dateTime));

      return previous ? replaceFrom(previous, fresh, start.getTime()) : fresh;
    },

    enabled: !!deviceId,
    refetchInterval: refetchIntervalMs,
    // Keep the last result while the same vehicle reloads for a new period, but never carry one
    // vehicle's readings over to another: they would sit under the new vehicle's name.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === deviceId ? previous : undefined,
  });
}
